import { useState } from 'react';
import { Check, ChevronsUpDown, FolderTree, LayoutDashboard, MoveDown, MoveUp, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { type Device, type Group } from '@/lib/traccar-api';
import { Checkbox } from '@/components/ui/checkbox';

export type DashboardWidgetId =
  | 'deviceStatus'
  | 'lastCommunication'
  | 'activeAlarms'
  | 'devicesInGeofences'
  | 'activity'
  | 'map';

const dashboardWidgetLabelKeys: Record<DashboardWidgetId, string> = {
  deviceStatus: 'dashboard.deviceStatus',
  lastCommunication: 'dashboard.lastCommunication',
  activeAlarms: 'dashboard.activeAlarms',
  devicesInGeofences: 'dashboard.devicesInGeofences',
  activity: 'dashboard.activity',
  map: 'dashboard.map',
};

interface HeaderControlsProps {
  lastUpdate: Date | null;
  devices?: Device[];
  groups?: Group[];
  selectedDeviceId?: number | null;
  selectedGroupId?: number | null;
  onDeviceFilterChange?: (deviceId: number | null) => void;
  onGroupFilterChange?: (groupId: number | null) => void;
  dashboardWidgetOrder?: DashboardWidgetId[];
  visibleDashboardWidgets?: DashboardWidgetId[];
  onToggleDashboardWidget?: (widget: DashboardWidgetId) => void;
  onMoveDashboardWidget?: (widget: DashboardWidgetId, direction: -1 | 1) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

export const HeaderControls = ({
  devices = [],
  groups = [],
  selectedDeviceId,
  selectedGroupId,
  onDeviceFilterChange,
  onGroupFilterChange,
  dashboardWidgetOrder,
  visibleDashboardWidgets = [],
  onToggleDashboardWidget,
  onMoveDashboardWidget,
  searchQuery = '',
  onSearchChange,
}: HeaderControlsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const sortedDevices = [...devices].sort((a, b) => a.name.localeCompare(b.name));
  const sortedGroups = [...groups].sort((a, b) => a.name.localeCompare(b.name));
  const selectedDevice = devices.find(d => d.id === selectedDeviceId);

  return (
    <>
      {onSearchChange && (
        <div className="relative hidden @lg:block">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('devices.searchPlaceholder')}
            className="pl-8 h-8 w-[180px] text-sm"
          />
        </div>
      )}
      {dashboardWidgetOrder && onToggleDashboardWidget && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              aria-label={t('dashboard.customize')}
              className="h-8 gap-2"
            >
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden @xl:inline">{t('dashboard.customize')}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-3">
            <p className="mb-2 text-sm font-medium">{t('dashboard.customize')}</p>
            <div className="space-y-1">
              {[...dashboardWidgetOrder, 'activity', 'map'].map((widget, index) => {
                const label = t(dashboardWidgetLabelKeys[widget]);
                const checked = visibleDashboardWidgets.includes(widget);

                return (
                  <div key={widget} className="flex items-center gap-2 rounded-sm px-1 py-1">
                    <Checkbox
                      checked={checked}
                      aria-label={t('dashboard.toggleCard', { card: label })}
                      onCheckedChange={() => onToggleDashboardWidget(widget)}
                    />
                    <span className={cn('min-w-0 flex-1 truncate text-sm', !checked && 'text-muted-foreground')}>
                      {label}
                    </span>
                    {onMoveDashboardWidget && index < dashboardWidgetOrder.length && (
                      <>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label={t('dashboard.moveCardUp', { card: label })}
                          disabled={index === 0}
                          onClick={() => onMoveDashboardWidget(widget, -1)}
                        >
                          <MoveUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label={t('dashboard.moveCardDown', { card: label })}
                          disabled={index === dashboardWidgetOrder.length - 1}
                          onClick={() => onMoveDashboardWidget(widget, 1)}
                        >
                          <MoveDown className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}
      {onGroupFilterChange && groups.length > 0 && (
        <Select
          value={selectedGroupId?.toString() ?? 'all'}
          onValueChange={(value) => onGroupFilterChange(value === 'all' ? null : Number(value))}
        >
          <SelectTrigger className="hidden @xl:flex w-[160px] text-sm gap-2">
            <FolderTree className="h-3 w-3 sm:h-4 sm:w-4" />
            <SelectValue placeholder={t('devices.allGroups')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('devices.allGroups')}</SelectItem>
            {sortedGroups.map((group) => (
              <SelectItem key={group.id} value={group.id.toString()}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {onDeviceFilterChange && devices.length > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              role="combobox"
              aria-expanded={open}
              className="w-[140px] @sm:w-[200px] justify-between text-xs @sm:text-sm font-normal"
            >
              <span className="truncate">
                {selectedDevice ? selectedDevice.name : t('devices.allDevices')}
              </span>
              <ChevronsUpDown className="h-3 w-3 @sm:h-4 @sm:w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[220px] p-0" align="end">
            <Command>
              <CommandInput placeholder={t('devices.searchPlaceholder')} className="h-9" />
              <CommandList>
                <CommandEmpty>{t('devices.noDevicesMatch')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="all"
                    onSelect={() => { onDeviceFilterChange(null); setOpen(false); }}
                  >
                    <Check className={cn('mr-2 h-4 w-4', selectedDeviceId == null ? 'opacity-100' : 'opacity-0')} />
                    {t('devices.allDevices')}
                  </CommandItem>
                  {sortedDevices.map((device) => (
                    <CommandItem
                      key={device.id}
                      value={device.name}
                      onSelect={() => { onDeviceFilterChange(device.id); setOpen(false); }}
                    >
                      <Check className={cn('mr-2 h-4 w-4', selectedDeviceId === device.id ? 'opacity-100' : 'opacity-0')} />
                      {device.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
};
